import { Box, Flex, Text } from "@chakra-ui/react";
import { useState, useEffect } from "react";
import UserDataStep from "./steps/UserDataStep";
import TicketDataStep from "./steps/TicketDataStep";
import SummaryStep from "./steps/SummaryStep";
import ConfirmationStep from "./steps/ConfirmationStep";
import { useLocation } from "react-router-dom";
import OrderSummary from "./steps/OrderSummary";
import EventDetailHeaderPage from "../../pages/EventDetailPage/EventDetailHeaderPage";
import { useTranslation } from "react-i18next";
import { Cookie } from "../../utils/cookie-utils";

const OrderFlow = () => {
    const { t } = useTranslation(["default", "invoice"]);
    const location = useLocation();
    const [step, setStep] = useState(0);
    const [maxStep, setMaxStep] = useState(0);
    const [formData, setFormData] = useState({});

    const steps = [
        t("order.tab.my_data"),
        t("order.tab.tickets_data"),
        t("order.tab.summary"),
        t("order.tab.payment"),
    ];

    const nextStep = () => {
        setStep((s) => {
            const next = Math.min(s + 1, steps.length - 1);
            setMaxStep((prev) => Math.max(prev, next));
            return next;
        });
    };
    const prevStep = () => setStep((s) => Math.max(s - 1, 0));

    const updateData = (data) => setFormData((prev) => ({ ...prev, ...data }));

    useEffect(() => {
        const initValues = async () => {
            window.scrollTo(0, 0);
            const event = location.state?.event || JSON.parse(localStorage.getItem("event"));
            const cart = location.state?.cart || JSON.parse(localStorage.getItem("cart"));
            if (!event || !cart) return;

            const eventId = event.id ?? event.eventId;
            const user = Cookie.get(`checkout:${eventId}:user`) || {};
            const att = Cookie.get(`checkout:${eventId}:attendees`) || {};

            setFormData({
                ...event,
                eventId,
                cart,
                ...user,
                ...att,
            });
        };
        initValues();
    }, [location.state]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    return (
        <Flex flexDirection="column" w="100%" alignItems="center" justifyContent="center" bgColor="white">
            <EventDetailHeaderPage event={formData} social={false} />

            {/* Kontener o maks. szerokości, z responsywnymi paddingami */}
            <Box
                w="100%"
                maxW="1200px"
                mx={{base: 0, md: "auto"}}
                px={{ base: 4, md: 8 }}
                py={{ base: 6, md: 10 }}
                bgColor="#FFFFFF"
                fontFamily="body"
            >
                {/* Kolumna na mobile, wiersz na desktopie */}
                <Flex direction={{ base: "column", lg: "row" }} gap={{ base: 6, lg: 12 }}>
                    {/* Lewa kolumna: kroki + zawartość kroku */}
                    <Flex w="full" direction="column" gap={{ base: 4, md: 6 }}>
                        {/* Krokowy nagłówek – poziomy scroll na mobile */}
                        {step !== 3 && (
                            <Flex
                                align="center"
                                mb={{ base: 2, md: 4 }}
                                gap={{ base: 4, md: 8 }}
                                overflowX="auto"
                                pb={{ base: 2, md: 0 }}
                                // lekkie wygładzenie scrolla na mobile
                                css={{
                                    scrollbarWidth: "thin",
                                    WebkitOverflowScrolling: "touch",
                                }}
                            >
                                {steps.slice(0, 3).map((label, index) => (
                                    <Flex key={`step-${index}`} align="center" gap={{ base: 3, md: 4 }}>
                                        <Flex
                                            gap={{ base: 2, md: 3 }}
                                            direction="row"
                                            align="center"
                                            opacity={index > step ? 0.5 : 1}
                                            onClick={() => {
                                                if (index <= maxStep) {
                                                    setStep(index);
                                                }
                                            }}
                                            cursor={index <= maxStep ? "pointer" : "default"}
                                            minW="fit-content"
                                        >
                                            <Box
                                                bg={index === step ? "#7A1F35" : "#F8F5F4"}
                                                color={index === step ? "#FFFFFF" : "#6E6064"}
                                                w={{ base: "28px", md: "32px" }}
                                                h={{ base: "28px", md: "32px" }}
                                                borderRadius="full"
                                                textAlign="center"
                                                lineHeight={{ base: "28px", md: "32px" }}
                                                fontWeight="bold"
                                                fontSize={{ base: "sm", md: "md" }}
                                                aria-current={index === step ? "step" : undefined}
                                            >
                                                {index + 1}
                                            </Box>
                                            <Text
                                                fontSize={{ base: "sm", md: "lg" }}
                                                fontWeight="600"
                                                color={index === step ? "#1E1517" : "#6E6064"}
                                                whiteSpace="nowrap"
                                            >
                                                {label}
                                            </Text>
                                        </Flex>

                                        {index < 2 && (
                                            <svg
                                                width="11"
                                                height="19"
                                                viewBox="0 0 11 19"
                                                fill="none"
                                                style={{ alignSelf: "center", flexShrink: 0 }}
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M2 2L9.5 9.5L2 17"
                                                    stroke="#6E6064"
                                                    strokeWidth="3"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        )}
                                    </Flex>
                                ))}
                            </Flex>
                        )}

                        {/* Zawartość kroków */}
                        {step === 0 && <UserDataStep data={formData} update={updateData} next={nextStep} t={t} />}
                        {step === 1 && <TicketDataStep data={formData} update={updateData} next={nextStep} prev={prevStep} t={t} />}
                        {step === 2 && <SummaryStep data={formData} next={nextStep} prev={prevStep} setStep={setStep} t={t} />}
                        {step === 3 && <ConfirmationStep data={formData} t={t} />}
                    </Flex>

                    {/* Prawa kolumna: podsumowanie (na mobile spada pod krokami) */}
                    {step !== 3 && (
                        <Box
                            w={{ base: "100%", lg: "380px" }}
                            // sticky tylko na desktopie
                            position={{ base: "static", lg: "sticky" }}
                            top={{ lg: "16px" }}
                            alignSelf={{ base: "stretch", lg: "start" }}
                        >
                            <OrderSummary data={formData} tickets={formData?.ticketTypes} update={updateData} t={t} />
                        </Box>
                    )}
                </Flex>
            </Box>
        </Flex>
    );
};

export default OrderFlow;
